import type { FieldValues } from 'react-hook-form';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

/**
 * A base class defining the contract for a handler in the form values processing chain.
 * This class supports a "Chain of Responsibility" pattern where multiple handlers
 * can react to form value changes in a specific, sequential order.
 */
export class FormValuesHandler {
    // Private subjects are used internally to push new values into the streams.
    private readonly valuesSubject$ = new Subject<FieldValues>();

    private readonly stableValuesOutputSubject$ = new Subject<FieldValues>();

    /**
     * A public Observable of the incoming values for internal processing by subclasses.
     */
    public readonly values$: Observable<FieldValues>;

    /**
     * A public Observable that emits form values after they have been processed
     * by this handler and deemed "stable" (i.e., no corrections were made).
     * This stream signals that the next handler in the chain can begin its processing.
     */
    public readonly stableValuesOutput$: Observable<FieldValues>;

    constructor() {
        // Expose the private subjects as read-only observables.
        this.values$ = this.valuesSubject$.asObservable();
        this.stableValuesOutput$ = this.stableValuesOutputSubject$.asObservable();
    }

    /**
     * The main input method for the handler. Pushes a new set of values
     * into the `values$` stream for processing.
     * @param values The new form values.
     */
    public updateValues(values: FieldValues): void {
        this.valuesSubject$.next(values);
    }

    /**
     * A protected method for subclasses to call when they have determined that
     * a set of values is stable. This signals completion to the orchestrator.
     * @param values The stable form values.
     */
    protected emitStable(values: FieldValues): void {
        this.stableValuesOutputSubject$.next(values);
    }
}
